<template>
  <QCard class="row q-py-md q-px-md" flat bordered>
    <div class="vehicle-image-container">
      <QImg
        v-if="showImage == 'trim'"
        :src="imagePathTrim"
        :cover="imagePathTrim"
        :contain="!imagePathTrim"
        :placeholder-src="placeholderUrl"
        spinner-color="white"
        class="vehicle-image-content trim"
        @error="handleImageError('trim')"
        trim
      />
      <QImg
        v-if="showImage == 'normal'"
        :src="imagePath"
        contain
        :placeholder-src="placeholderUrl"
        spinner-color="white"
        class="vehicle-image-content normal"
        @error="handleImageError('normal')"
        normal
      />
      <QImg
        v-if="showImage == 'base'"
        :src="imagePathBase"
        contain
        :placeholder-src="placeholderUrl"
        spinner-color="white"
        class="vehicle-image-content base"
        @error="handleImageError('base')"
      />
    </div>
    <div class="vehicle-details-container">
      <div class="vehicle-details-content">
        <div class="vehicle-info">
          <p class="text-grey-8 vehicle-info-fueltype font-nunito">{{ fuelType }}</p>
          <p class="text-secondary vehicle-info-title">{{ vehicleName }}</p>
          <p class="text-grey-8 vehicle-info-text">{{ vehicleDescription }}</p>
        </div>
        <div class="vehicle-pricing">
          <p class="text-grey-8 vehicle-pricing-text vehicle-pricing-text--label">
            <label>
              {{ displayWeeklyPrice ? 'Estimated weekly payment' : 'Estimated monthly payment' }}
            </label>
          </p>
          <div class="vehicle-pricing__row">
            <span class="text-tertiary vehicle-pricing-amount">
              {{ displayWeeklyPrice ? formattedWeekly : formattedMonthly }} {{ GSTText }}
              <span
                role="button"
                tabindex="0"
                class="vehicle-pricing-asterisk"
                @click="showPricingInfo"
                @keydown.enter.prevent="showPricingInfo"
                >*</span
              >
            </span>
            <template v-if="showPaymentBreakdown && pbd_isPaymentBreakdownsEnabled">
              <div class="vehicle-pricing__divider" aria-hidden="true" />
              <PaymentBreakdownButton :vehicle="vehicle" :pay-cycle="repaymentOption" @click="showPaymentBreakdownModal = true" />
            </template>
          </div>
        </div>
      </div>

      <div class="action-group-container">
        <p class="rrp-text">
          RRP: {{ formattedRRP }}
          <QIcon :name="infoIcon" class="info-icon cursor-pointer" size="20px" @click="showInformation"></QIcon>
        </p>
        <div class="row q-col-gutter-md">
          <div class="col-xs-6 col-sm-6 col-md-12 col-lg-12">
            <CalculatorButton :tertiaryBorder="true" label="View vehicle" rounded styleProp="width:100%" @click="handleViewVehicle" />
          </div>
          <div class="col-xs-6 col-sm-6 col-md-12 col-lg-12">
            <CheckboxButton
              class="shortlist-button"
              color="tertiary"
              :label="shortlistedLabel"
              v-model="isShortlisted"
              :tertiaryBorder="true"
              outlined
              rounded
              fullWidth
              @input="handleInput"
              :disabled="!isShortlisted && disabled"
            />
          </div>
          <div class="col-xs-12 col-sm-12 col-md-12 col-lg-12 vehicle-result-card__enquire-col">
            <CalculatorButton
              color="primary"
              rounded
              hasChevronIcon
              label="Enquire now"
              outlined
              removeOpacity
              @click="handleEnquireNow"
              styleProp="width:100%"
            />
          </div>
        </div>
      </div>
    </div>

    <PaymentBreakdown
      v-if="showPaymentBreakdown && pbd_isPaymentBreakdownsEnabled"
      v-model="showPaymentBreakdownModal"
      :vehicle-finance-amount="pbd_cardParts ? pbd_cardParts.finance : ''"
      :bundled-running-costs-amount="pbd_cardParts ? pbd_cardParts.bundled : ''"
      :estimated-payment="pbd_cardEstimatedPayment"
      :payment-total-label="pbd_cardTotalLabel"
      :payment-cadence="displayWeeklyPrice ? 'weekly' : 'monthly'"
      @onClose="showPaymentBreakdownModal = false"
    />
  </QCard>
</template>

<script>
import { QCard, QIcon, QImg } from 'eclipx.ui';
import { evaAlertCircleOutline } from '@quasar/extras/eva-icons';
import CheckboxButton from '../CheckboxButton';
import CalculatorButton from '../CalculatorButton';
import PaymentBreakdownButton from '../PaymentBreakdownButton.vue';
import PaymentBreakdown from '../Modal/PaymentBreakdown.vue';
import { PaymentBreakdownMixin } from '../../../mixins/calculator';

export default {
  name: 'VehicleResultCard',
  mixins: [PaymentBreakdownMixin],
  components: { QCard, QImg, QIcon, CheckboxButton, CalculatorButton, PaymentBreakdownButton, PaymentBreakdown },
  props: {
    imagePath: {
      type: String
    },
    imagePathTrim: {
      type: String
    },
    imagePathBase: {
      type: String
    },
    fuelType: {
      type: String
    },
    vehicleName: {
      type: String
    },
    vehicleDescription: {
      type: String
    },
    weeklyPrice: {
      type: Number,
      default: 0
    },
    monthlyPrice: {
      type: Number,
      default: 0
    },
    rrp: {
      type: Number,
      default: 0
    },
    repaymentOption: {
      type: String,
      required: true,
      default: 'weekly'
    },
    showGST: {
      type: Boolean,
      default: false
    },
    value: {
      type: Boolean,
      default: false
    },
    catalogVehicleId: {
      type: String,
      required: true
    },
    vehicleMake: {
      type: String,
      required: true
    },
    vehicleModel: {
      type: String,
      required: true
    },
    vehicleTrim: {
      type: String,
      default: ''
    },
    vehicleDrivetrain: {
      type: String,
      default: ''
    },
    disabled: {
      type: Boolean,
      default: false
    },
    vehicleFinance: {
      type: Number,
      default: null
    },
    vehicleFinanceWeekly: {
      type: Number,
      default: null
    },
    vehicleRunningCost: {
      type: Number,
      default: null
    },
    vehicleRunningCostWeekly: {
      type: Number,
      default: null
    },
    /** When false, the PaymentBreakdownButton + modal never render (used by shortlist surfaces). */
    showPaymentBreakdown: {
      type: Boolean,
      default: true
    },
    vehicle: {
      type: Object,
      default: null
    }
  },
  data() {
    return {
      isShortlisted: this.value,
      shortlistedLabel: this.value ? 'Shortlisted' : 'Shortlist',
      displayWeeklyPrice: this.repaymentOption === 'weekly',
      placeholderUrl: `${process.env.BASE_URL}assets/images/vehicleImagePlaceholder.svg`,
      showImage: 'trim',
      showPaymentBreakdownModal: false
    };
  },
  computed: {
    /** Required by PaymentBreakdownMixin (`pbd_cardParts`) */
    pbd_ratebookRow() {
      return {
        vehicleFinance: this.vehicleFinance,
        vehicleFinanceWeekly: this.vehicleFinanceWeekly,
        vehicleRunningCost: this.vehicleRunningCost,
        vehicleRunningCostWeekly: this.vehicleRunningCostWeekly
      };
    },
    isNZ() {
      const countryCode = this.orgProfile?.region || 'NZ';
      return countryCode === 'NZ';
    },
    formattedWeekly: function () {
      return this.formatCurrent(this.weeklyPrice);
    },
    formattedMonthly: function () {
      return this.formatCurrent(this.monthlyPrice);
    },
    formattedRRP: function () {
      return this.formatCurrent(this.rrp);
    },
    GSTText: function () {
      if (this.showGST) {
        return this.isNZ ? '(ex. gst)' : '(incl. gst)';
      }

      return '';
    }
  },
  methods: {
    formatCurrent: function (value) {
      // return value.toLocaleString('en-AU', { style: 'currency', currency: this.isNZ ? 'NZD' : 'AUD' });
      return `$${value.toLocaleString('en-AU', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
    },
    handleInput() {
      this.$emit('input', this.isShortlisted);
    },
    handleImageError(imageType) {
      if (imageType == 'trim') {
        this.showImage = 'normal';
      } else if (imageType == 'normal') {
        this.showImage = 'base';
      }
    },
    handleViewVehicle() {
      this.$router.push({
        name: 'vehicleProduct',
        params: {
          catalogVehicleId: this.catalogVehicleId,
          vehicleMake: this.vehicleMake,
          vehicleModel: this.vehicleModel,
          vehicleTrim: this.vehicleTrim ? this.vehicleTrim.toUpperCase() : undefined,
          vehicleDrivetrain: this.vehicleDrivetrain ? this.vehicleDrivetrain.toUpperCase() : undefined
        },
        query: {
          ...this.$route.query
        }
      });
    },
    showInformation() {
      this.$emit('onInfoIconClick');
    },
    showPricingInfo() {
      this.$emit('onPricingInfoIconClick');
    },
    handleEnquireNow() {
      setTimeout(() => {
        this.$emit('onHandleEnquireNow');
      });
    }
  },
  watch: {
    value(newValue) {
      this.isShortlisted = newValue;
    },
    isShortlisted(newValue) {
      this.shortlistedLabel = newValue ? 'Shortlisted' : 'Shortlist';
    },
    repaymentOption(newValue) {
      this.displayWeeklyPrice = newValue === 'weekly';
    }
  },
  created() {
    this.infoIcon = evaAlertCircleOutline;
  }
};
</script>

<style lang="sass" scoped>
.vehicle-image-container
  margin-right: 10px
  height: 194px
  max-width: 300px
  width: 100%

.vehicle-image-content
  height: 194px
  width: 100%

.vehicle-details-container
  width: calc(100% - 310px)
  display: flex
  flex-flow: row wrap
  justify-content: space-between

  .vehicle-details-content
    width: 100%
    max-width: calc(100% - 230px);
    text-wrap: pretty
    display: flex
    flex-flow: column wrap
    justify-content: space-between

.vehicle-info-text
  font-size: 14px

.vehicle-info-fueltype
  font-size: 16px
  font-weight: 700

.vehicle-info-title
  font-size: 22px
  font-weight: 700
  margin: 0 0 10px

.vehicle-pricing-text
  font-size: 14px
  margin: 0 !important

.vehicle-pricing-text--label label
  cursor: default

.vehicle-pricing__row
  display: flex
  flex-wrap: wrap
  align-items: center
  gap: 10px 12px
  margin-top: 5px

.vehicle-pricing__divider
  width: 5px
  height: 28px
  // background: var(--color-neutral-grey-4, #e0e0e0)
  flex-shrink: 0

.vehicle-pricing__bundled-note
  font-size: 12px
  margin: 8px 0 0
  line-height: 1.4

.vehicle-pricing-amount
  font-size: 18px
  font-weight: 800

.vehicle-pricing-asterisk
  cursor: pointer
  margin-left: 2px

.action-group-container
  max-width: 200px;
  width: 100%;
  display: flex
  flex-flow: column wrap
  justify-content: space-between
  margin: 10px 10px 10px 0

.rrp-text
  display: flex
  flex-flow: row wrap
  align-items: center
  font-weight: 700
  justify-content: space-between

.info-icon
  margin-left: 5px
  rotate: 180deg

.calculator-button
  min-width: 150px

// Full-width primary CTA on xs/sm (stacked under View vehicle + Shortlist).
.vehicle-result-card__enquire-col .calculator-button
  min-width: 0
  width: 100%

@media (max-width: 1024px)
  .vehicle-details-content
    max-width: 100%!important
    width: 100%
  .action-group-container
    width: 100%
    max-width: 100%
    .shortlist-button
      align-self: end
      max-width: 200px
      display: inline;

@media (max-width: 647px)
  .vehicle-image-container
    margin-right: 0
    margin-bottom: 10px
    max-width: 768px
    .vehicle-image-content
      max-width: 768px
  .vehicle-details-container
    width: 100%

  .action-group-container .shortlist-button
    margin-top: 10px
    max-width: calc(50% + 1em)
    display: inline;

@media (max-width: 374px)
  .action-group-container .shortlist-button
    max-width: 100%
    display: inline;
</style>
